@if (errorMessage) {
  <tidy-error-message
    [text]="errorMessage" />
}

@if (windowService.isDesktopRes) {
  <!-- Title + rental-type tabs, then a second header with the controls —
       the same two-header paradigm the Contacts page uses. -->
  <tidy-desktop-header class="no-bottom-margin">
    <div class="wrappable-container compliance-tabs-row">
      <tidy-text [title]="'Compliance'" />
      @if (isReportVisible) {
        <tidy-tabs
          [tabs]="rentalTypeOptions"
          [activeTab]="selectedRentalType"
          (tabChange)="selectRentalType($event)" />
      }
    </div>
  </tidy-desktop-header>

  <tidy-desktop-header #complianceHeader>
    <div class="wrappable-container">
      @if (isReportVisible) {
        <tidy-text [header]="selectedRentalTypeLabel" />
      }
      <tidy-header-select
        [items]="addressItems"
        [(ngModel)]="selectedPropertyValue"
        (optionChange)="onDesktopPropertyChange()"
        [icon]="'assets/svg/home-outline.svg'" />
      @if (selectedAddressId && (complianceCheckStatus === null || complianceCheckStatus === 'failed')) {
        <tidy-small-button
          [text]="'Run Compliance Check'"
          [image]="'assets/img/help-circle-outline-white.svg'"
          (buttonClick)="triggerComplianceCheck()" />
      }
      <tidy-small-button
        [text]="'Add Item'"
        [image]="'assets/img/add-button-white.svg'"
        (buttonClick)="goToAddComplianceItem()" />
      <tidy-small-button
        [text]="'Completed Items'"
        [image]="'assets/svg/checkmark-circle-outline.svg'"
        (buttonClick)="goToCompletedCompliance()" />
      @if (showFilters) {
        <tidy-search-input
          class="header-search"
          [ngModel]="searchQuery"
          [ngModelOptions]="{ standalone: true }"
          (valueChange)="onSearchChange($event)"
          [placeholder]="'Search items'"
          [icon]="'assets/svg/search-outline.svg'" />
        <tidy-header-select
          class="compliance-filters__status"
          [items]="statusFilterItems"
          [ngModel]="statusFilter"
          [ngModelOptions]="{ standalone: true }"
          (optionChange)="onStatusFilterChange($event)"
          [icon]="'assets/svg/funnel-outline.svg'" />
        @if (hasActiveFilters) {
          <tidy-text
            class="compliance-filters__clear link"
            [actionBody]="'Clear'"
            (action)="clearFilters()" />
        }
      }
    </div>
    <div class="non-wrappable-right-aligned-container">
      <automations-preview-button
        [tag]="'compliance'" />
    </div>
  </tidy-desktop-header>
}

@if (!windowService.isDesktopRes && loaded) {
  <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 10px;">
    <tidy-small-button
      [text]="'Add Item'"
      [image]="'assets/img/add-button-white.svg'"
      (buttonClick)="goToAddComplianceItem()" />
    <tidy-small-button
      [text]="'Completed Items'"
      [image]="'assets/svg/checkmark-circle-outline.svg'"
      (buttonClick)="goToCompletedCompliance()" />
  </div>
  <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 10px; flex-wrap: wrap;">
    <tidy-header-select
      [items]="addressItems"
      [(ngModel)]="selectedPropertyValue"
      (optionChange)="onDesktopPropertyChange()"
      [icon]="'assets/svg/home-outline.svg'" />
    @if (selectedAddressId && (complianceCheckStatus === null || complianceCheckStatus === 'failed')) {
      <tidy-small-button
        [text]="'Run Check'"
        [image]="'assets/img/help-circle-outline-white.svg'"
        (buttonClick)="triggerComplianceCheck()" />
    }
  </div>
}

@if (!loaded) {
  <div style="width: 100%; margin-bottom: 12px;">
    <ion-skeleton-text animated="true" style="height: 40px; border-radius: 6px" />
  </div>
  <div style="width: 100%; margin-bottom: 8px;">
    <ion-skeleton-text animated="true" style="height: 36px; border-radius: 4px" />
  </div>
  @for (i of [1,2,3,4,5]; track i) {
    <div style="width: 100%; margin-bottom: 6px;">
      <ion-skeleton-text animated="true" style="height: 44px; border-radius: 4px" />
    </div>
  }
}

<!-- Pending check state -->
@if (loaded && (complianceCheckStatus === 'pending' || complianceCheckStatus === 'processing')) {
  <tidy-card class="check-progress">
    <div class="check-progress__hero">
      <div class="check-progress__icon-wrap">
        <span class="check-progress__pulse"></span>
        <span class="check-progress__pulse check-progress__pulse--delayed"></span>
        <tidy-image
          [src]="'assets/svg/shield-checkmark-outline.svg'"
          class="check-progress__icon no-right-margin" />
      </div>
      <div class="check-progress__copy">
        <div class="check-progress__title">Running compliance check</div>
        <div class="check-progress__subtitle">
          We're reviewing local rules for this address. This usually takes 2&ndash;10 minutes &mdash; feel free to leave this page, we'll keep working.
        </div>
      </div>
    </div>

    <ul class="check-progress__steps">
      @for (step of loadingSteps; track step.label; let i = $index) {
        <li
          class="check-progress__step"
          [class.is-active]="i === currentLoadingStepIndex"
          [class.is-done]="i < currentLoadingStepIndex">
          <span class="check-progress__step-marker">
            @if (i < currentLoadingStepIndex) {
              <tidy-image
                [src]="'assets/svg/checkmark-outline.svg'"
                class="check-progress__step-check no-right-margin" />
            } @else if (i === currentLoadingStepIndex) {
              <ion-spinner name="dots" />
            }
          </span>
          <span class="check-progress__step-label">{{ step.label }}</span>
        </li>
      }
    </ul>

    <div class="check-progress__footer">
      <span class="check-progress__elapsed-dot"></span>
      <span>Elapsed {{ elapsedDisplay }}</span>
    </div>
  </tidy-card>

  <div class="check-progress__skeletons" aria-hidden="true">
    @for (i of [1, 2, 3]; track i) {
      <div class="check-progress__skeleton-card">
        <div class="check-progress__skeleton-row">
          <ion-skeleton-text animated="true" style="width: 32px; height: 32px; border-radius: 50%;" />
          <div style="flex: 1;">
            <ion-skeleton-text animated="true" style="width: 60%; height: 14px; border-radius: 4px;" />
            <ion-skeleton-text animated="true" style="width: 40%; height: 10px; margin-top: 6px; border-radius: 4px;" />
          </div>
          <ion-skeleton-text animated="true" style="width: 24px; height: 14px; border-radius: 4px;" />
        </div>
      </div>
    }
  </div>
}

<!-- Failed check state -->
@if (loaded && complianceCheckStatus === 'failed' && !report) {
  <tidy-alert>
    <tidy-row>
      <tidy-text [body]="'Compliance check failed. Please try running the check again.'" />
    </tidy-row>
  </tidy-alert>
}

<!-- No-check empty state -->
@if (loaded && selectedAddressId && !report && (complianceCheckStatus === null)) {
  <tidy-alert>
    <tidy-row>
      <tidy-text [body]="noCheckMessage" /><span>&nbsp;</span>
      <tidy-text
        [actionBody]="'Run Compliance Check'"
        (action)="triggerComplianceCheck()"
        class="white-link" />
    </tidy-row>
  </tidy-alert>
}

<!-- Completed check but no report (e.g. only stale checks exist for this address) -->
@if (loaded && selectedAddressId && !report && complianceCheckStatus === 'completed') {
  <tidy-alert>
    <tidy-row>
      <tidy-text [body]="'We couldn\'t find compliance results for this property.'" /><span>&nbsp;</span>
      <tidy-text
        [actionBody]="'Run Compliance Check'"
        (action)="triggerComplianceCheck()"
        class="white-link" />
    </tidy-row>
  </tidy-alert>
}

<!-- Display plan view -->
@if (loaded && selectedAddressId && report && complianceCheckStatus !== 'pending' && complianceCheckStatus !== 'processing') {
  <div class="compliance-display">

    <!-- Desktop renders both of these in the two headers above. -->
    @if (!windowService.isDesktopRes) {
      <!-- STR / LTR tabs -->
      <tidy-tabs
        [tabs]="rentalTypeOptions"
        [activeTab]="selectedRentalType"
        (tabChange)="selectRentalType($event)" />

      <!-- Search + status filter -->
      @if (hasAnyItems || hasActiveFilters) {
        <div class="compliance-filters">
          <tidy-search-input
            class="compliance-filters__search match-tidy-input"
            [ngModel]="searchQuery"
            [ngModelOptions]="{ standalone: true }"
            (valueChange)="onSearchChange($event)"
            [placeholder]="'Search items'"
            [icon]="'assets/svg/search-outline.svg'"
            [fixedWidth]="false" />
          <tidy-header-select
            class="compliance-filters__status"
            [items]="statusFilterItems"
            [ngModel]="statusFilter"
            [ngModelOptions]="{ standalone: true }"
            (optionChange)="onStatusFilterChange($event)"
            [icon]="'assets/svg/funnel-outline.svg'" />
          @if (hasActiveFilters) {
            <tidy-text
              class="compliance-filters__clear link"
              [actionBody]="'Clear'"
              (action)="clearFilters()" />
          }
        </div>
      }
    }

    <!-- Eligibility verdict card -->
    @if (filteredReport.eligibility; as eligibility) {
      <tidy-card
        class="eligibility-card"
        [ngStyle]="{
          '--eligibility-color': eligibilityColors[eligibility.status] || eligibilityColors['unknown'],
          '--eligibility-bg': eligibilityBackgrounds[eligibility.status] || eligibilityBackgrounds['unknown']
        }">
        <div class="eligibility-card__head">
          <span class="eligibility-card__badge">
            {{ eligibilityLabels[eligibility.status] || eligibilityLabels['unknown'] }}
          </span>
        </div>
        @if (eligibility.title) {
          <div class="eligibility-card__title">{{ eligibility.title }}</div>
        }
        @if (eligibility.summary) {
          <div class="eligibility-card__summary">{{ eligibility.summary }}</div>
        }
        @if (eligibility.confidence) {
          <div class="eligibility-card__meta">{{ eligibility.confidence | titlecase }} confidence</div>
        }
      </tidy-card>
    }

    <!-- Prohibited callout -->
    @if (filteredReport.eligibility?.status === 'prohibited') {
      <div class="prohibited-callout">
        <tidy-image
          [src]="'assets/svg/alert-circle-outline.svg'"
          class="prohibited-callout__icon" />
        <div class="prohibited-callout__text">
          This rental type is not allowed at this address. Resolve any blockers below before pursuing alternatives.
        </div>
      </div>
    }

    <!-- Summary strip -->
    @if (hasAnyItems) {
      <div class="summary-strip">
        <span>{{ report.visible_item_count }} items</span>
        <span class="summary-strip__dot"></span>
        <span>{{ visibleSections.length + hiddenSections.length }} categories</span>
        @if (report.generated_at) {
          <span class="summary-strip__dot"></span>
          <span>Updated {{ report.generated_at | date:'MMM d, yyyy' }}</span>
        }
      </div>
    }

    <!-- Taxes moved to their own Finance → Taxes tab -->
    @if (report) {
      <tidy-alert>
        <tidy-row class="vertical-align-center">
          <tidy-image [src]="'assets/svg/cash-outline.svg'" class="body-size" />
          <tidy-text [body]="'Taxes for this property are managed in Finance → Taxes.'" />
        </tidy-row>
        <tidy-row class="extra-top-padding">
          <tidy-small-button
            [text]="'View Taxes'"
            (buttonClick)="goToTaxes()" />
        </tidy-row>
      </tidy-alert>
    }

    <!-- No items in any section -->
    @if (!hasAnyItems) {
      <tidy-alert>
        <tidy-row>
          <tidy-text [body]="'No compliance items match this rental type.'" />
        </tidy-row>
      </tidy-alert>
    } @else if (hasActiveFilters && !visibleSections.length) {
      <tidy-alert>
        <tidy-row class="vertical-align-center">
          <tidy-text [body]="'No items match the current filters.'" /><span>&nbsp;</span>
          <tidy-text
            class="link"
            [actionBody]="'Clear filters'"
            (action)="clearFilters()" />
        </tidy-row>
      </tidy-alert>
    }

    <!-- Visible sections (default + not-hidden_by_default) -->
    @for (section of visibleSections; track section.id) {
      <tidy-card class="section-card">
        <div
          class="section-card__header"
          role="button"
          [attr.aria-expanded]="!!expandedSections[section.id]"
          (click)="toggleSection(section.id)">
          <span
            class="section-card__icon-bubble"
            [ngStyle]="{ '--section-bg': sectionAccents[section.id] || sectionAccents['admin'] }">
            <tidy-image
              [src]="sectionIcons[section.id] || sectionIcons['admin']"
              class="no-right-margin" />
          </span>
          <div class="section-card__title-block">
            <span class="section-card__title">
              {{ section.title || sectionFallbackTitles[section.id] || section.id }}
            </span>
            @if (section.description) {
              <span class="section-card__description">{{ section.description }}</span>
            }
          </div>
          <span class="section-card__count">{{ section.items.length }}</span>
          @if (sectionHasIncomplete(section)) {
            <span class="section-card__action" (click)="$event.stopPropagation()">
              <tidy-small-button
                [text]="bulkSavingSectionId === section.id ? 'Saving…' : 'Mark all'"
                [image]="'assets/svg/checkmark-done-outline.svg'"
                [disabled]="bulkSavingSectionId === section.id"
                (buttonClick)="markAllInSectionComplete(section)" />
            </span>
          }
          <tidy-image
            [src]="'assets/svg/chevron-down-outline.svg'"
            class="section-card__chevron"
            [class.is-open]="expandedSections[section.id]" />
        </div>

        @if (expandedSections[section.id]) {
          <div class="section-card__body">
            @for (item of section.items; track item.id) {
              <div
                class="item-row"
                role="button"
                [class.is-completed]="item.status === 'completed'"
                (click)="goToItemDetail(item.id)">
                <div class="item-row__main">
                  <div class="item-row__description">
                    {{ item.what_to_do || item.name || 'Compliance item' }}
                  </div>
                  <span class="item-row__action" (click)="$event.stopPropagation()">
                    @if (item.status === 'completed') {
                      <tidy-small-button
                        [text]="'Reopen'"
                        [image]="'assets/svg/refresh-outline.svg'"
                        (buttonClick)="quickToggleItemComplete(item)" />
                    } @else {
                      <tidy-small-button
                        [text]="'Mark Complete'"
                        [primary]="true"
                        [image]="'assets/svg/checkmark-circle-outline.svg'"
                        (buttonClick)="quickToggleItemComplete(item)" />
                    }
                  </span>
                </div>
                @if (item.source_name || item.jurisdiction || item.next_due_at) {
                  <div class="item-row__meta">
                    @if (item.source_name || item.jurisdiction) {
                      <span class="item-row__chip">
                        <tidy-image
                          [src]="'assets/svg/location-outline.svg'"
                          class="item-row__chip-icon no-right-margin" />
                        {{ item.source_name || item.jurisdiction }}
                      </span>
                    }
                    @if (item.next_due_at) {
                      <span class="item-row__chip">
                        <tidy-image
                          [src]="'assets/svg/calendar-outline.svg'"
                          class="item-row__chip-icon no-right-margin" />
                        Due {{ item.next_due_at | date:'MMM d' }}
                      </span>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </tidy-card>
    }

    <!-- Hidden-by-default sections, behind a single dashed toggle -->
    @if (hiddenSections.length) {
      <div
        class="hidden-sections-toggle"
        role="button"
        [attr.aria-expanded]="showHiddenSections"
        (click)="toggleHiddenSections()">
        <span class="hidden-sections-toggle__label">
          {{ showHiddenSections ? 'Hide recommendations & admin' : 'Show recommendations & admin (' + hiddenSections.length + ')' }}
        </span>
        <tidy-image
          [src]="'assets/svg/chevron-down-outline.svg'"
          class="hidden-sections-toggle__chevron"
          [class.is-open]="showHiddenSections" />
      </div>

      @if (showHiddenSections) {
        @for (section of hiddenSections; track section.id) {
          <tidy-card class="section-card">
            <div
              class="section-card__header"
              role="button"
              [attr.aria-expanded]="!!expandedSections[section.id]"
              (click)="toggleSection(section.id)">
              <span
                class="section-card__icon-bubble"
                [ngStyle]="{ '--section-bg': sectionAccents[section.id] || sectionAccents['admin'] }">
                <tidy-image
                  [src]="sectionIcons[section.id] || sectionIcons['admin']"
                  class="no-right-margin" />
              </span>
              <div class="section-card__title-block">
                <span class="section-card__title">
                  {{ section.title || sectionFallbackTitles[section.id] || section.id }}
                </span>
                @if (section.description) {
                  <span class="section-card__description">{{ section.description }}</span>
                }
              </div>
              <span class="section-card__count">{{ section.items.length }}</span>
              <tidy-image
                [src]="'assets/svg/chevron-down-outline.svg'"
                class="section-card__chevron"
                [class.is-open]="expandedSections[section.id]" />
            </div>

            @if (expandedSections[section.id]) {
              <div class="section-card__body">
                @for (item of section.items; track item.id) {
                  <div class="item-row">
                    <div class="item-row__description">
                      {{ item.what_to_do || item.name || 'Compliance item' }}
                    </div>
                    <div class="item-row__meta">
                      <span
                        class="item-row__chip is-status"
                        [ngStyle]="{
                          '--chip-bg': statusBackgrounds[item.status] || statusBackgrounds['not_started'],
                          '--chip-fg': statusColors[item.status] || statusColors['not_started']
                        }">
                        <span class="item-row__chip-dot"></span>
                        {{ statusLabels[item.status] || item.status }}
                      </span>
                      @if (item.applies_to && item.applies_to !== 'both') {
                        <span class="item-row__chip">{{ item.applies_to }}</span>
                      }
                      @if (item.source_name) {
                        <span class="item-row__chip">{{ item.source_name }}</span>
                      } @else if (item.jurisdiction) {
                        <span class="item-row__chip">{{ item.jurisdiction }}</span>
                      }
                      @if (item.next_due_at) {
                        <span class="item-row__chip">
                          Due {{ item.next_due_at | date:'MMM d' }}
                        </span>
                      }
                      @if (item.confidence) {
                        <span
                          class="item-row__chip"
                          [ngStyle]="{ color: confidenceColors[item.confidence] || '#999' }">
                          <span class="item-row__chip-dot"></span>
                          {{ item.confidence | titlecase }}
                        </span>
                      }
                    </div>
                    <div class="item-row__actions">
                      @if (item.status === 'completed') {
                        <tidy-small-button
                          [text]="'Reopen'"
                          [image]="'assets/svg/refresh-outline.svg'"
                          (buttonClick)="quickToggleItemComplete(item)" />
                      } @else {
                        <tidy-small-button
                          [text]="'Mark Complete'"
                          [primary]="true"
                          [image]="'assets/svg/checkmark-outline.svg'"
                          (buttonClick)="quickToggleItemComplete(item)" />
                      }
                      <tidy-small-button
                        [text]="'Details'"
                        [image]="'assets/svg/arrow-forward-outline.svg'"
                        (buttonClick)="goToItemDetail(item.id)" />
                    </div>
                  </div>
                }
              </div>
            }
          </tidy-card>
        }
      }
    }

  </div>
}

@if (showAboutThisPage) {
  <tidy-about-this-page
    [page]="'compliance'"
    [overlayTop]="overlayTop"
    (dismissed)="dismissAboutThisPage()" />
}
